import React from 'react';
import PositionTreemap from './charts/PositionTreemap';
import RiskProfileRadar from './charts/RiskProfileRadar';
import ScenarioBars from './charts/ScenarioBars';
import HoldingsTable from './HoldingsTable';
import EngineOutputHeader from './EngineOutputHeader';
import ExposureTable from './ExposureTable';
import ScenarioTable from './ScenarioTable';
import type { AccountAnalysis } from '../../services/riskService';

interface Props {
  analysis: AccountAnalysis;
  mode: string;
  runAt: string;
  accountLabel?: string;
}

const RiskAnalysisView: React.FC<Props> = ({ analysis, mode, runAt, accountLabel }) => {
  const a = analysis as any;
  const engineOutput       = a.engine_output;
  const flagsTable         = a.flags_table || [];
  const exposureTable      = a.exposure_table || [];
  const scenarioTable      = a.scenario_table || [];
  const portfolioNarrative = a.portfolio_narrative || '';
  const scenarioNarrative  = a.scenario_narrative || '';
  const holdingsTable      = a.holdings_table || [];
  const charts             = a.charts;

  const hasAnyChart =
    (charts?.risk_profile?.data?.length > 0) ||
    (charts?.scenario_bars?.data?.length > 0) ||
    (charts?.position_weight?.data?.length > 0) ||
    (charts?.directional_exposure?.data?.length > 0);

  return (
    <div className="risk-analysis-view">
      {/* Charts on top -- dashboard-style visual summary */}
      {hasAnyChart && (
        <section className="risk-charts-section">
          <div className="chart-grid-2">
            {charts?.risk_profile?.data && charts.risk_profile.data.length > 0 && (
              <div className="chart-card">
                <h4 className="chart-title">
                  Risk profile (0–100)
                  <span
                    className="chart-help"
                    title="Radar chart of 5 risk dimensions, each normalized to 0–100. Directional = |net delta| as % of NAV. Concentration = 2× top-1 position % (25% -> 50). Leverage = margin utilization %. Beta amp = |beta-adjusted exposure − net delta| × 3, showing how much high-beta names amplify your directional read. Tail = |worst scenario P&L %|. Bigger polygon = riskier profile."
                  >?</span>
                </h4>
                <RiskProfileRadar data={charts.risk_profile.data} />
              </div>
            )}
            {charts?.scenario_bars?.data && charts.scenario_bars.data.length > 0 && (
              <div className="chart-card">
                <h4 className="chart-title">
                  Scenario P&amp;L
                  <span
                    className="chart-help"
                    title="Estimated P&L (% of NAV) under 5 canned market scenarios, sorted worst market move first. Uses the engine's delta + gamma + vega decomposition. Red ≤ -20%, amber ≤ -5%, green ≥ +5%. Compare downside magnitudes with upside capture to see how symmetric your book is."
                  >?</span>
                </h4>
                <ScenarioBars data={charts.scenario_bars.data} />
              </div>
            )}
            {charts?.position_weight?.data && charts.position_weight.data.length > 0 && (
              <div className="chart-card">
                <h4 className="chart-title">
                  Position weight (mark value / NAV)
                  <span
                    className="chart-help"
                    title="Each tile is one underlying, sized by |mark-to-market value / NAV|. Sums the current market value of every position (equity + option premium) rolled up by underlying. Blue = equity-only or mixed underlying, cyan = options-only. Answers: how much of my book is in each name?"
                  >?</span>
                </h4>
                <PositionTreemap data={charts.position_weight.data} />
              </div>
            )}
            {charts?.directional_exposure?.data && charts.directional_exposure.data.length > 0 && (
              <div className="chart-card">
                <h4 className="chart-title">
                  Directional exposure (delta by underlying)
                  <span
                    className="chart-help"
                    title="Each tile is one underlying, sized by |net delta contribution / NAV|. Combines equity weight + signed option delta into one number per name. Answers: which underlying is driving my market exposure? Large tile here + small tile in Position weight = a leveraged options bet."
                  >?</span>
                </h4>
                <PositionTreemap data={charts.directional_exposure.data} />
              </div>
            )}
          </div>
        </section>
      )}

      {engineOutput && (
        <EngineOutputHeader
          engineOutput={engineOutput}
          flagsTable={flagsTable}
          accountLabel={accountLabel}
        />
      )}

      {exposureTable.length > 0 && (
        <ExposureTable rows={exposureTable} portfolioNarrative={portfolioNarrative} />
      )}

      {scenarioTable.length > 0 && (
        <ScenarioTable rows={scenarioTable} scenarioNarrative={scenarioNarrative} />
      )}

      {holdingsTable.length > 0 && (
        <HoldingsTable rows={holdingsTable} />
      )}

      <p className="analysis-meta">
        As of{' '}
        {new Date(runAt).toLocaleString('en-US', { timeZone: 'America/New_York' })}{' '}
        ET · {mode}
      </p>
    </div>
  );
};

export default RiskAnalysisView;
